import { Component, Inject, DOCUMENT, ChangeDetectionStrategy } from '@angular/core';

import { HttpResponse } from '@angular/common/http';
import { NgxSpinnerService } from 'ngx-spinner';
import { TradService } from './trad.service';
import { FileService } from '../../file/file.service';
import { AttestService } from '../../attest/attest.service';
import { saveAs } from 'file-saver-es';
import { fadeInAnimation } from '../../_animations/fade-in.animation';
import { ActivatedRoute, Router } from '@angular/router';
import { Trad } from '../../interfaces/trad.interface';
import { ErrorModalService } from '../../modal/error.modal.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Beslut } from '../../interfaces/beslut.interface';
import { TradMeddelande } from '../../interfaces/tradMeddelande.interface';
import { FormControl, Validators, FormBuilder, FormGroup } from '@angular/forms';
import { Attest } from '../../interfaces/attest.interface';
import { FiliDb } from '../../interfaces/filiDb.interface';
import { HuvudB2 } from '../../interfaces/huvudB2.interface';
import { NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { NgbdB2BilagorModal } from '../../modal/b2-bilagor.modal.component';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { SessionService } from '../../session/session.service';
import { AuthService } from '../../auth/auth.service';
import { NotificationModalComponent } from '../../modal/notification.modal.component';


@Component({
    selector: 'app-trad',
    templateUrl: './trad.component.html',
    styleUrls: ['./trad.component.scss'],
    animations: [
        fadeInAnimation
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})

export class TradComponent {

  constructor(
    protected service: TradService,
    protected auth: AuthService,
    protected spinner: NgxSpinnerService,
    protected sessionService: SessionService,
    protected errorModalService: ErrorModalService,
    protected fileService: FileService,
    protected attestService: AttestService,
    protected route: ActivatedRoute,
    protected fb: FormBuilder,
    protected modalService: NgbModal,
    protected router: Router,
    @Inject(DOCUMENT) private readonly document: Document) {
    this.loadTrad(this.route.snapshot.paramMap.get('id'));
  }

  public get messageMaxLength(): number { return 6000 };
  public get antalMeddelandenSomVisas(): number { return 4 }; // visa bara 4 meddelanden som default

  public get isLoaded(): boolean { return !!this.trad };

  public trad: Trad;

  private lockedWindow: NgbModalRef;

  public userForm: FormGroup;

  public get getB2Beslut() { return this.trad.b2Lista.map(b => b.huvudB2) };
  public get getBeslut() { return this.trad.beslutLista.map(b => b.lgBeslut) };

  public b2BeslutOrderName: string = 'datum';
  public b2BeslutOrder: string[] = ['datum'];
  public beslutOrderName: string = 'datum';
  public beslutOrder: string[] = [ 'datum' ];
  public b2BeslutReverse: boolean = false;
  public beslutReverse: boolean = false;
  public visaAllaMeddelanden: boolean = false;

  public get selectedBesluts(): Beslut[] { return this.trad.beslutLista.filter(b => b.lgBeslut.isSelected && b.lgBeslut.fil).map(b => b.lgBeslut); }
  public get selectedB2Besluts(): HuvudB2[] { return this.trad.b2Lista.filter(b => b.huvudB2.isSelected && b.huvudB2.fil).map(b => b.huvudB2); }

  public get selectedFiles(): FiliDb[] { return this.selectedBesluts.map(b => b.fil).concat(this.selectedB2Besluts.map(b => b.fil)); }

  public get signedBesluts(): Beslut[] { return this.trad.beslutLista.filter(b => b.lgBeslut.arSignerad).map(b => b.lgBeslut); }
  public get signedB2Besluts(): HuvudB2[] { return this.trad.b2Lista.filter(b => b.huvudB2.arSignerad).map(b => b.huvudB2); }

  public get messageForm() { return this.userForm.get('message') };

  public get message() { return this.messageForm.value; }
  public set message(value: string) { this.messageForm.setValue(value); }

  private loadTrad(id: string): void {

    const doLoad = () => {
      setTimeout(() => {
        if (!this.trad) {
          this.spinner.show();
        }
      }, 300);

      this.service.get(this.sessionService.activeInstance.id, id).subscribe(
        (trad: Trad) => {
          this.spinner.hide();
          this.trad = trad;
          this.message = '';
        },
        (error) => {
          this.spinner.hide();
          this.errorModalService.open('Inläsningsfel', 'Kunde inte hämta det begärda meddelandet.');
        });
    }

    if (!this.sessionService.hasSession) {
      this.router.navigate(['/login']);
      return;
    }

    if (!this.sessionService.hasActiveInstance) {
      this.sessionService.selectInstance(doLoad);
      return;
    }

    doLoad();
  }

  openNotificationModal() {
    const modalRef = this.modalService.open(NotificationModalComponent, { centered: true });

    modalRef.componentInstance.title = 'modal.notis-title';
    modalRef.componentInstance.message = 'modal.notis-message';
    modalRef.componentInstance.okText = 'modal.notis-ok';
  }

  public reloadTrad() {
    this.spinner.show();
    this.loadTrad(this.trad.id);

    if (!this.message)
    {
      this.openNotificationModal();
    }
  }

  public ngOnInit(): void {
    this.userForm = this.fb.group({ 'message': new FormControl('', Validators.compose([Validators.maxLength(this.messageMaxLength), Validators.required])) });
  }

  public get instanceName(): string {
    return this.sessionService.activeInstance.name;
  }

  public createMessage(): void {

    this.spinner.show();

    this.service.createMessage(this.sessionService.activeInstance.id, this.trad.id, this.message).subscribe(
      (isCommitted: boolean) => {
        this.reloadTrad();
        this.spinner.hide();
      },
      (error) => {
        this.errorModalService.open('Inläsningsfel', 'Kunde inte skapa ett nytt inlägg.');
        this.spinner.hide();
      });
  }

  public attesteraBeslut(): void {

    this.spinner.show();

    this.attestService.attestManyTradBeslut(
      this.sessionService.activeInstance.id,
      this.trad.id,
      this.selectedBesluts.map(b => b.id),
      this.selectedB2Besluts.map(b => b.id)).subscribe(
      (attest: Attest) => {
        if (attest) {
          this.selectedBesluts.map(b => {
            b.attest = attest;
            b.isSelected = false;
          });
          this.selectedB2Besluts.map(b2 => {
            b2.attest = attest;
            b2.isSelected = false;
          });
        }
        this.spinner.hide();
      },
      (error) => {
        this.errorModalService.open('Inläsningsfel', 'Kunde inte attestera de valda besluten.');
        this.spinner.hide();
      });
  }

  public showAttachments(b2: HuvudB2): void {
    this.openModal(NgbdB2BilagorModal, { centered: true });
    this.lockedWindow.componentInstance.arende = this.trad.arende;
    this.lockedWindow.componentInstance.b2 = b2;
  }

  private openModal(content, windowClass?: NgbModalOptions): void {
    this.closeModal(this.lockedWindow);
    this.lockedWindow = this.modalService.open(content, windowClass);
  }

  private closeModal(modalRef: NgbModalRef): void {
    if (!!modalRef) {
      modalRef.close();
    }
  }

  public getFile(file: FiliDb): void {

    this.spinner.show();

    this.fileService.getFile(this.sessionService.activeInstance.id, file.id).subscribe(
      (response: HttpResponse<ArrayBuffer>) => {
        this.spinner.hide();
        const contentType = response.headers.get('content-type');
        const blob = new Blob([response.body], { type: contentType });
        saveAs(blob, file.filnamn + file.filtillagg);
      },
      (error) => {
        this.errorModalService.open('Inläsningsfel', 'Kunde inte hämta den begärda filen.');
        this.spinner.hide();
      });
  }

  public setB2BeslutOrder(name: string, order: string[]): void {
    if (this.b2BeslutOrderName === name) {
      this.b2BeslutReverse = !this.b2BeslutReverse;
    }

    this.b2BeslutOrderName = name;
    this.b2BeslutOrder = order.slice();
  }

  public setBeslutOrder(name: string, order: string[]): void {
    if (this.beslutOrderName === name) {
      this.beslutReverse = !this.beslutReverse;
    }

    this.beslutOrderName = name;
    this.beslutOrder = order.slice();
  }

  public getSum(items: any[], propertyPath: string): number {
    let sum = 0;
    for (let i = 0; i < items.length; i++) {
      sum += this.resolve(propertyPath, items[i]) || 0;
    }
    return sum;
  }

  public scrollToTable(id1: string, id2: string): void {
    function offset(el): number {
      const rect = el.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      return rect.top + scrollTop;
    }

    this.route.queryParamMap.subscribe(paramMap => {
      const targetElement = this.document.querySelector(`table[id = '${id1}'], table[id = '${id2}']`);
      if (targetElement) {
        const top = offset(targetElement) - (window.innerHeight / 5);
        window.scrollTo({
          top: top,
          behavior: 'smooth'
        });
      }
    });
  }

  private resolve(propertyPath, baseObject): any {
    return propertyPath.split('.').reduce((p, q) => {
      return p ? p[q] : null;
    }, baseObject || self);
  }
}
